import { useEffect, useRef, useState } from 'react'

export interface ColumnOption {
  key: string
  header: string
}

interface ColumnPickerProps {
  options: ColumnOption[]
  visibleKeys: string[]
  defaultKeys: string[]
  onChange: (keys: string[]) => void
}

export default function ColumnPicker({ options, visibleKeys, defaultKeys, onChange }: ColumnPickerProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const toggle = (key: string) => {
    const next = visibleKeys.includes(key)
      ? visibleKeys.filter(item => item !== key)
      : // Preserve the canonical column order rather than append order, so the
        // table layout does not depend on the order boxes were ticked.
        options.filter(option => option.key === key || visibleKeys.includes(option.key)).map(option => option.key)
    // Never allow an empty table.
    if (next.length === 0) return
    onChange(next)
  }

  const isDefault =
    visibleKeys.length === defaultKeys.length && visibleKeys.every((key, index) => key === defaultKeys[index])

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        className="mobile-tap-target rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2 text-sm font-semibold text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
      >
        Columns ({visibleKeys.length}/{options.length})
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-60 rounded-2xl border border-white/10 bg-[#0b0b12] p-2 shadow-2xl">
          <div className="max-h-80 overflow-auto custom-scrollbar">
            {options.map(option => {
              const checked = visibleKeys.includes(option.key)
              return (
                <label
                  key={option.key}
                  className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.06]"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(option.key)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className={checked ? 'text-white' : ''}>{option.header}</span>
                </label>
              )
            })}
          </div>
          <button
            type="button"
            disabled={isDefault}
            onClick={() => onChange(defaultKeys)}
            className="mt-1 w-full rounded-xl px-3 py-2 text-left text-xs font-semibold text-indigo-300 hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent"
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  )
}
